// ─── Section Manager ──────────────────────────────────────────
// Manages section CRUD, reordering, and per-section settings.
// Supports both canvas (collage) and widget section types.

import React, { useState, useRef } from 'react';
import {
  ChevronUp, ChevronDown, ChevronRight, Plus, Trash2, Eye, EyeOff,
  Image as ImageIcon, Palette, Layout, Clock, Grid3X3, MessageSquare,
  MapPin, Type, Quote, HelpCircle, Upload, Play
} from 'lucide-react';

const SECTION_TYPE_META = {
  hero:            { icon: ImageIcon,    label: 'Hero',          color: 'rose' },
  collage:         { icon: Layout,        label: 'Canvas',        color: 'blue' },
  timeline:        { icon: Clock,         label: 'Timeline',      color: 'purple' },
  gallery:         { icon: Grid3X3,       label: 'Gallery',       color: 'green' },
  rsvp:            { icon: MessageSquare,  label: 'Response Card', color: 'rose' },
  'event-details': { icon: MapPin,        label: 'Event Details',  color: 'amber' },
  story:           { icon: Type,          label: 'Story',          color: 'teal' },
  quote:           { icon: Quote,         label: 'Quote',          color: 'indigo' },
  faq:             { icon: HelpCircle,    label: 'FAQ',            color: 'orange' },
  countdown:       { icon: Clock,         label: 'Countdown',      color: 'red' },
  venue:           { icon: MapPin,        label: 'Venue',          color: 'emerald' },
  footer:          { icon: Layout,        label: 'Footer',         color: 'gray' },
  slideshow:       { icon: Play,          label: 'Slideshow',      color: 'cyan' },
};

const ADDABLE_TYPES = [
  { type: 'hero', label: 'Hero', desc: 'Full-screen intro with photo' },
  { type: 'collage', label: 'Canvas', desc: 'Free drag/drop area' },
  { type: 'timeline', label: 'Timeline', desc: 'Event schedule' },
  { type: 'gallery', label: 'Gallery', desc: 'Photo grid' },
  { type: 'rsvp', label: 'Response Card', desc: 'Response form' },
  { type: 'event-details', label: 'Event Details', desc: 'Date, time, venue' },
  { type: 'story', label: 'Story', desc: 'Text + image block' },
  { type: 'venue', label: 'Venue', desc: 'Location with map' },
  { type: 'countdown', label: 'Countdown', desc: 'Timer to event' },
  { type: 'slideshow', label: 'Slideshow', desc: 'Auto-sliding image carousel' },
];

const SECTION_DEFAULTS = {
  hero: () => ({
    type: 'hero',
    label: 'Hero',
    variant: 'hero-centered-image',
    visible: true,
    animation: { title: 'stagger-text', subtitle: 'fade-up', cta: 'fade-up', image: 'zoom-soft' },
    editableRules: {
      fields: ['introLabel', 'title', 'subtitle', 'dateText', 'backgroundImage', 'ctaText', 'ctaTarget'],
      locked: ['layout', 'spacing'],
    },
    data: {
      introLabel: "We're getting married",
      title: 'Your Names',
      subtitle: '',
      dateText: '',
      backgroundImage: '',
      ctaText: 'Open Invitation',
      ctaTarget: 'rsvp',
    },
  }),
  collage: () => ({
    type: 'collage',
    label: 'Canvas',
    visible: true,
    height: { mobile: 800, desktop: 800 },
    background: { type: 'color', color: '#ffffff' },
    editableRules: { fields: ['elements', 'background', 'height'], locked: [] },
    data: {},
    elements: [],
  }),
  timeline: () => ({
    type: 'timeline',
    label: 'Timeline',
    variant: 'timeline-vertical',
    visible: true,
    data: {
      eyebrow: 'SCHEDULE',
      title: 'Our Day',
      events: [
        { time: '3:00 PM', title: 'Ceremony', description: 'Join us as we say our vows' },
        { time: '4:30 PM', title: 'Cocktails', description: 'Drinks and appetizers' },
        { time: '6:00 PM', title: 'Reception', description: 'Dinner and dancing' },
      ],
    },
  }),
  gallery: () => ({
    type: 'gallery',
    label: 'Gallery',
    variant: 'gallery-grid',
    visible: true,
    data: { title: 'Our Moments', images: [] },
  }),
  rsvp: () => ({
    type: 'rsvp',
    label: 'RSVP',
    variant: 'rsvp-card',
    visible: true,
    data: {
      title: 'Will You Join Us?',
      subtitle: "We'd love to celebrate with you",
    },
  }),
  'event-details': () => ({
    type: 'event-details',
    label: 'Event Details',
    variant: 'details-grid',
    visible: true,
    data: {
      title: 'Event Details',
      items: [
        { icon: 'calendar', label: 'Date', value: 'June 15, 2027' },
        { icon: 'clock', label: 'Time', value: '3:00 PM' },
        { icon: 'map-pin', label: 'Venue', value: 'The Garden Estate' },
      ],
    },
  }),
  story: () => ({
    type: 'story',
    label: 'Our Story',
    variant: 'story-single-column',
    visible: true,
    data: { title: 'Our Story', body: '' },
  }),
  venue: () => ({
    type: 'venue',
    label: 'Venue',
    variant: 'venue-card',
    visible: true,
    data: { name: 'Venue Name', address: '', mapUrl: '' },
  }),
  countdown: () => ({
    type: 'countdown',
    label: 'Countdown',
    variant: 'countdown-centered',
    visible: true,
    data: { title: 'Days Until We Say I Do', targetDate: '' },
  }),
  slideshow: () => ({
    type: 'slideshow',
    label: 'Slideshow',
    visible: true,
    data: { images: [], transition: 'slide', speed: 'medium', autoplay: true, aspectRatio: '16/9' },
  }),
};

// Color presets for quick background color selection
const BG_PRESETS = [
  '#ffffff', '#f8f8f8', '#f5f0eb', '#f2ece6', '#ece7de',
  '#1a1a1a', '#111111', '#0d0d0d', '#2a2220', '#1c241f',
  '#F6EFEC', '#F5F3EC', '#F8F4EC',
];

function SectionCard({ section, index, total, onMove, onUpdate, onRemove, onBgUpload, uploading }) {
  const [expanded, setExpanded] = useState(false);
  const meta = SECTION_TYPE_META[section.type] || SECTION_TYPE_META.collage;
  const Icon = meta.icon;
  const fileRef = useRef(null);

  const handleBgFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onBgUpload(section.id, file);
  };

  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden transition-all"
      data-testid={`section-card-${section.id}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2">
        <div className={`w-6 h-6 rounded flex items-center justify-center bg-${meta.color}-100 dark:bg-${meta.color}-900/30`}>
          <Icon className={`w-3.5 h-3.5 text-${meta.color}-600 dark:text-${meta.color}-400`} />
        </div>
        <button
          onClick={() => setExpanded(e => !e)}
          className="flex-1 text-left text-xs font-semibold text-gray-800 dark:text-gray-200 truncate"
        >
          {section.label || meta.label}
        </button>
        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 font-medium uppercase">
          {meta.label}
        </span>

        {/* Move buttons */}
        <button
          onClick={() => onMove(section.id, 'up')}
          disabled={index === 0}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-20 transition-colors"
          data-testid={`move-up-${section.id}`}
        >
          <ChevronUp className="w-3 h-3 text-gray-500" />
        </button>
        <button
          onClick={() => onMove(section.id, 'down')}
          disabled={index === total - 1}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-20 transition-colors"
          data-testid={`move-down-${section.id}`}
        >
          <ChevronDown className="w-3 h-3 text-gray-500" />
        </button>

        {/* Visibility */}
        <button
          onClick={() => onUpdate(section.id, { visible: !section.visible })}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          data-testid={`toggle-vis-${section.id}`}
        >
          {section.visible
            ? <Eye className="w-3 h-3 text-green-500" />
            : <EyeOff className="w-3 h-3 text-gray-400" />
          }
        </button>

        {/* Expand arrow */}
        <button
          onClick={() => setExpanded(e => !e)}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <ChevronRight className={`w-3 h-3 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>
      </div>

      {/* Expanded: Background + Height */}
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-gray-100 dark:border-gray-800 space-y-3">
          {/* Label */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Label</label>
            <input
              type="text"
              value={section.label || ''}
              onChange={e => onUpdate(section.id, { label: e.target.value })}
              className="mt-1 w-full px-2 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 outline-none focus:ring-1 focus:ring-blue-400"
              data-testid={`section-label-${section.id}`}
            />
          </div>

          {/* Background */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Background</label>
            <div className="mt-1 flex gap-1.5">
              <button
                onClick={() => onUpdate(section.id, { background: { type: 'color', color: section.background?.color || '#ffffff' } })}
                className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                  section.background?.type !== 'image'
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200'
                }`}
              >
                <Palette className="w-3 h-3" /> Color
              </button>
              <button
                onClick={() => fileRef.current?.click()}
                className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                  section.background?.type === 'image'
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200'
                }`}
              >
                <ImageIcon className="w-3 h-3" /> {uploading ? 'Uploading...' : 'Image'}
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleBgFileChange} />
            </div>

            {/* Color picker */}
            {section.background?.type !== 'image' && (
              <div className="mt-2 space-y-2">
                <div className="flex gap-1 flex-wrap">
                  {BG_PRESETS.map(c => (
                    <button
                      key={c}
                      onClick={() => onUpdate(section.id, { background: { type: 'color', color: c } })}
                      className={`w-5 h-5 rounded-full border-2 transition-all ${
                        section.background?.color === c ? 'border-blue-500 scale-110' : 'border-gray-200 dark:border-gray-600'
                      }`}
                      style={{ background: c }}
                    />
                  ))}
                </div>
                <input
                  type="color"
                  value={section.background?.color || '#ffffff'}
                  onChange={e => onUpdate(section.id, { background: { type: 'color', color: e.target.value } })}
                  className="w-full h-7 rounded cursor-pointer"
                />
              </div>
            )}

            {/* Image preview */}
            {section.background?.type === 'image' && section.background?.src && (
              <div className="mt-2 relative rounded-md overflow-hidden h-16">
                <img src={section.background.src} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="text-[10px] text-white/90 font-medium px-2 py-1 bg-black/40 rounded"
                  >
                    Replace
                  </button>
                </div>
              </div>
            )}

            {/* Overlay for images */}
            {section.background?.type === 'image' && (
              <div className="mt-2">
                <label className="text-[10px] text-gray-500">Overlay opacity</label>
                <input
                  type="range"
                  min="0"
                  max="80"
                  value={parseInt(section.background?.overlayOpacity || '0')}
                  onChange={e => {
                    const op = parseInt(e.target.value);
                    onUpdate(section.id, {
                      background: {
                        ...section.background,
                        overlay: op > 0 ? `rgba(0,0,0,${op / 100})` : undefined,
                        overlayOpacity: op,
                      }
                    });
                  }}
                  className="w-full h-1 mt-1"
                />
              </div>
            )}
          </div>

          {/* Height (canvas sections only) */}
          {section.type === 'collage' && (
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Mobile Height</label>
                <input
                  type="number"
                  value={section.height?.mobile || 700}
                  onChange={e => onUpdate(section.id, { height: { ...section.height, mobile: parseInt(e.target.value) || 700 } })}
                  className="mt-1 w-full px-2 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 outline-none"
                  min={200}
                  max={3000}
                  step={50}
                />
              </div>
              <div className="flex-1">
                <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Desktop Height</label>
                <input
                  type="number"
                  value={section.height?.desktop || 800}
                  onChange={e => onUpdate(section.id, { height: { ...section.height, desktop: parseInt(e.target.value) || 800 } })}
                  className="mt-1 w-full px-2 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 outline-none"
                  min={200}
                  max={3000}
                  step={50}
                />
              </div>
            </div>
          )}

          {/* Delete */}
          <button
            onClick={() => {
              if (window.confirm(`Remove "${section.label || meta.label}" section?`)) {
                onRemove(section.id);
              }
            }}
            className="w-full py-1.5 rounded-md text-[10px] font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
            data-testid={`remove-section-${section.id}`}
          >
            <Trash2 className="w-3 h-3 inline mr-1" /> Remove Section
          </button>
        </div>
      )}
    </div>
  );
}

export default function SectionManager({
  sections, onAddSection, onRemoveSection, onMoveSection, onUpdateSection, onBgUpload, uploadingSectionId,
}) {
  const [showAddMenu, setShowAddMenu] = useState(false);

  return (
    <div className="space-y-2" data-testid="section-manager">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
          Sections
        </h3>
        <span className="text-[10px] text-gray-400">{sections.length}</span>
      </div>

      {/* Section list */}
      <div className="space-y-1.5">
        {sections.map((section, idx) => (
          <SectionCard
            key={section.id}
            section={section}
            index={idx}
            total={sections.length}
            onMove={onMoveSection}
            onUpdate={onUpdateSection}
            onRemove={onRemoveSection}
            onBgUpload={onBgUpload}
            uploading={uploadingSectionId === section.id}
          />
        ))}
      </div>

      {/* Add Section */}
      <div className="relative">
        <button
          onClick={() => setShowAddMenu(v => !v)}
          className="w-full py-2 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 text-xs font-medium text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-500 dark:hover:border-blue-600 dark:hover:text-blue-400 transition-colors flex items-center justify-center gap-1.5"
          data-testid="add-section-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Add Section
        </button>

        {showAddMenu && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden max-h-[320px] overflow-y-auto">
            {ADDABLE_TYPES.map(t => {
              const meta = SECTION_TYPE_META[t.type] || {};
              const Icon = meta.icon || Layout;
              return (
                <button
                  key={t.type}
                  onClick={() => {
                    onAddSection(t.type);
                    setShowAddMenu(false);
                  }}
                  className="w-full px-3 py-2 flex items-center gap-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
                  data-testid={`add-section-${t.type}`}
                >
                  <Icon className="w-4 h-4 text-gray-400" />
                  <div>
                    <div className="text-xs font-medium text-gray-800 dark:text-gray-200">{t.label}</div>
                    <div className="text-[10px] text-gray-400">{t.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export { SECTION_DEFAULTS };
